import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { get } from 'global/lib/lodash';
import { Icon, IconButton } from '@mui/material';
import { withRouter } from 'react-router-dom';
import { Addedition } from './styles';
import { Column, Row } from 'global/components/row';
import { Text } from 'global/components/text';
import { Button } from 'global/components/Button';
import { _size } from 'global/utils';
import SnackAlerts from 'global/components/snack-alerts';
import EditionList from '../../document/EditionList';
import DocumentList from '../../document/DocumentList';
import { EditionAccessProvider } from '../../document/EditionAccess';
import Document from '../../document/Document';
import FlexibleLayout from 'global/components/FlexibleLayout';
import { AddExistingSplitShell } from 'global/components/FlexibleLayout/styles';
import { CenterLoader } from 'global/styles';
import useBodyOverflow from 'global/hooks/useBodyOverflow';

export const AddEditionsList = withRouter((props) => {
  const { doc } = props;
  const { editionDocuments } = doc;

  const { setBodyOverflow } = useBodyOverflow();


  const [state, setState] = useState({
    items: editionDocuments,
    selectedEdition: undefined,
    selectedDoc: undefined,
    showList: false,
    selectedDocs: {},
  });

  const [alerts, setAlerts] = useState();

  const edid = get(state, 'selectedEdition.id');
  const classid = get(state, 'selectedDoc.classificationId');

  const edClick = (ed) => {
    setState((s) => ({ ...s, selectedEdition: ed }));
  };

  const docClick = (doc) => {
    setState((s) => ({ ...s, selectedDoc: doc }));
  };

  const addDoc = () => {
    setState((s) => ({
      ...s,
      selectedDocs: {
        ...s.selectedDocs,
        [get(state, 'selectedDoc.id')]: state.selectedDoc,
      },
    }));
  };

  const removeDoc = (k) => {
    setState((s) => {
      delete s.selectedDocs[k];
      return {
        ...s,
      };
    });
  };

  const submit = async () => {
    try {
      setState((s) => ({ ...s, loading: true }));
      await props.addDocs(Object.keys(state.selectedDocs).map((k) => state.selectedDocs[k]));
      props.onClose(true);
    } catch (err) {
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const showEdition = !state.selectedEdition;
  const showDocList = !!state.selectedEdition;
  const showDoc = state.selectedDoc;

  useEffect(() => {
    setBodyOverflow(true);

    return () => {
      setBodyOverflow(false);
    };
  }, [showEdition, showDoc, showDocList]);

  const closeDocList = useCallback(() => {
    setState((s) => ({ ...s, selectedDoc: null, selectedEdition: null }));
  }, []);

  const closeDocument = useCallback(() => {
    setState((s) => ({ ...s, selectedDoc: null }));
  }, []);

  const docListCloseProps = useMemo(
    () => ({ component: undefined, to: undefined, onClick: closeDocList }),
    [closeDocList]
  );

  const documentCloseProps = useMemo(
    () => ({ component: undefined, to: undefined, onClick: closeDocument }),
    [closeDocument]
  );

  if (state.loading) {
    return (
      <Addedition>
        <CenterLoader />
      </Addedition>
    );
  }

  if (state.showList) {
    return (
      <Addedition>
        <Column center style={{ padding: 8, marginTop: 16 }}>
          <Text type="headline_medium" bold>{`You have selected ${_size(state.selectedDocs)} documents`}</Text>
          <Column style={{ padding: 8 }}>
            {Object.keys(state.selectedDocs).map((k) => {
              return (
                <Row>
                  <IconButton onClick={() => removeDoc(k)} size="large">
                    <Icon>close</Icon>
                  </IconButton>
                  <Text className="docText">{get(state.selectedDocs, `${k}.document.classification.system.name`)}</Text>
                  <Text className="docText">{get(state.selectedDocs, `${k}.document.name`)}</Text>
                  <Text className="docText">{get(state.selectedDocs, [k, 'documentableType'])}</Text>
                </Row>
              );
            })}
          </Column>

          <Row center className="listButtons">
            <Button variant="text" onClick={() => setState((s) => ({ ...s, showList: false }))} startIcon={<Icon>close</Icon>}>
              BACK TO ADD
            </Button>
            <Button onClick={submit} startIcon={<Icon>save</Icon>} color="secondary">
              ADD TO EDITION
            </Button>
          </Row>
        </Column>

        <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
      </Addedition>
    );
  }

  return (
    <Addedition>
      {showEdition && <EditionList showCourses={false} showCoursesInList={true} showReview={false} isModal={true} onClick={edClick} />}

      {showDocList && (
        <EditionAccessProvider editionId={state.selectedEdition.id} skipRedirect>
          <AddExistingSplitShell>
            {/* Same split as /library: list width follows showRightPanel, not the URL.
                Modal stays on account/content/edition/:id; selection is local state. */}
            <FlexibleLayout
              variant="sidebar"
              fullWidthContent
              showRightPanel={!!showDoc}
              leftPanel={
                <DocumentList
                  isModal={true}
                  closeProps={docListCloseProps}
                  onClick={docClick}
                  libid={state.selectedEdition.id}
                  path={`document/${get(state, 'selectedDoc.classificationId')}`}
                  isDocumentPath={!!showDoc}
                />
              }
              rightPanel={
                showDoc ? (
                  <Document classificationId={classid} onDoc={docClick} isModal editionId={edid} closeProps={documentCloseProps} />
                ) : null
              }
            />
          </AddExistingSplitShell>
        </EditionAccessProvider>
      )}

      <div className="buffer" />

      <div className={'buttonContainer'}>
        <Row className="buttonRow">
          <Text>{`${_size(state.selectedDocs)} Selected Documents`}</Text>
          <Button disabled={!showDoc} onClick={addDoc} startIcon={<Icon>add_circle</Icon>}>
            ADD THIS DOCUMENT
          </Button>
          <Button disabled={!_size(state.selectedDocs)} onClick={() => setState((s) => ({ ...s, showList: true }))} startIcon={<Icon>double_arrow</Icon>}>
            REVIEW ADDITIONS
          </Button>
        </Row>
      </div>

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </Addedition>
  );
});
